<script setup>
// Auto-extracted chrome for Waermepumpevergleich.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Wärmepumpe Vergleich" },
    brandTagline: { type: String, default: "Modelle direkt gegenüberstellen." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Wärmepumpe Vergleich");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Modelle direkt gegenüberstellen.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');

</script>

<template>
    <main class="wpv-root" :style="`--wpv-accent:${brandAccent};`">
        <header class="wpv-topbar">
                    <div class="wpv-topbar-inner">
                        <div class="wpv-brandblock">
                            <span class="wpv-brandmark" aria-hidden="true">
                                <span class="wpv-brandmark-bar"></span>
                                <span class="wpv-brandmark-bar wpv-brandmark-bar--mid"></span>
                                <span class="wpv-brandmark-bar wpv-brandmark-bar--short"></span>
                            </span>
                            <div class="wpv-brandtext">
                                <strong>{{ brandName }}</strong>
                                <span>{{ brandTagline }}</span>
                            </div>
                        </div>
                        <div class="wpv-databadge">KfW-Wärmepumpenliste als Referenz</div>
                    </div>
                </header>
        <slot />
        <footer class="wpv-foot">
                    <div class="wpv-foot-inner">
                        <p v-if="author" class="wpv-byline">
                            Redaktionell verantwortet von <strong>{{ author.name }}</strong>
                            <span v-if="author.role"> - {{ author.role }}</span>
                        </p>
                        <p class="wpv-foot-note">
                            Quelle: <a href="https://www.kfw.de" target="_blank" rel="nofollow noopener">KfW - Liste der förderfähigen Wärmepumpen</a>. SCOP-Werte und Leistungsangaben aus dem öffentlich zugänglichen Datensatz; kein Ersatz für ein verbindliches Angebot.
                        </p>
                        <p class="wpv-foot-links">
                            <a href="/ratgeber">Ratgeber</a>
                            <span aria-hidden="true">·</span>
                            <a href="/impressum">Impressum</a>
                            <span aria-hidden="true">·</span>
                            <a href="/datenschutz">Datenschutz</a>
                            <span aria-hidden="true">·</span>
                            <a href="/agb">AGB</a>
                            <span aria-hidden="true">·</span>
                            <a href="/widerruf">Widerruf</a>
                        </p>
                    </div>
                </footer>
    </main>
</template>

<style scoped>

.wpv-root {
    --wpv-blue: v-bind(brandAccent);
    --wpv-blue-deep: #0c4a6e;
    --wpv-blue-soft: #e0f2fe;
    --wpv-ink: #0b1220;
    --wpv-ink-mute: #475569;
    --wpv-paper: #fafaf9;
    --wpv-line: #e2e8f0;
    --wpv-gold: #fde68a;
    --wpv-best: #ecfeff;
    --wpv-best-line: #06b6d4;

    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: var(--wpv-ink);
    background: var(--wpv-paper);
    line-height: 1.55;
}

.wpv-topbar {
    border-bottom: 1px solid var(--wpv-line);
    background: #fff;
}
.wpv-topbar-inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 14px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.wpv-brandblock {
    display: flex;
    align-items: center;
    gap: 12px;
}
.wpv-brandmark {
    display: inline-flex;
    flex-direction: column;
    gap: 3px;
    width: 28px;
    height: 28px;
    justify-content: center;
}
.wpv-brandmark-bar {
    height: 4px;
    background: var(--wpv-blue);
    width: 28px;
    border-radius: 1px;
}
.wpv-brandmark-bar--mid { width: 20px; }
.wpv-brandmark-bar--short { width: 12px; }
.wpv-brandtext {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
}
.wpv-brandtext strong {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: -0.01em;
}
.wpv-brandtext span {
    font-size: 12px;
    color: var(--wpv-ink-mute);
}
.wpv-databadge {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--wpv-blue-deep);
    background: var(--wpv-blue-soft);
    border: 1px solid #bae6fd;
    padding: 6px 10px;
    border-radius: 999px;
    font-weight: 600;
}

.wpv-hero {
    max-width: 1180px;
    margin: 0 auto;
    padding: 64px 24px 32px;
}
.wpv-hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
    gap: 48px;
    align-items: start;
}
@media (max-width: 880px) {
    .wpv-hero-grid { grid-template-columns: 1fr; gap: 32px; }
}
.wpv-hero-kicker {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 12px;
    font-weight: 600;
    color: var(--wpv-blue-deep);
    margin: 0 0 18px;
}
.wpv-hero-title {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: clamp(34px, 5vw, 58px);
    line-height: 1.02;
    letter-spacing: -0.025em;
    margin: 0 0 22px;
}
.wpv-hero-lede {
    font-size: 18px;
    color: var(--wpv-ink-mute);
    max-width: 580px;
    margin: 0 0 26px;
}
.wpv-hero-bullets {
    list-style: none;
    padding: 0;
    margin: 0 0 28px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.wpv-hero-bullets li {
    display: flex;
    align-items: baseline;
    gap: 14px;
    font-size: 15px;
}
.wpv-bullet-num {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: var(--wpv-blue);
    background: var(--wpv-blue-soft);
    padding: 4px 8px;
    border-radius: 4px;
    min-width: 36px;
    text-align: center;
}
.wpv-hero-microcopy {
    font-size: 13px;
    color: var(--wpv-ink-mute);
    margin: 18px 0 0;
    max-width: 520px;
}

.wpv-cta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: var(--wpv-blue);
    color: #fff;
    border: 2px solid var(--wpv-blue);
    padding: 18px 32px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 14px;
    border-radius: 2px;
    cursor: pointer;
    transition: background .15s, transform .1s;
}
.wpv-cta:hover { background: var(--wpv-blue-deep); border-color: var(--wpv-blue-deep); }
.wpv-cta:active { transform: translateY(1px); }
.wpv-cta:disabled { opacity: .5; cursor: not-allowed; }
.wpv-cta--block { width: 100%; justify-content: center; }
.wpv-cta-arrow { font-size: 18px; line-height: 1; }

.wpv-hero-card {
    background: #fff;
    border: 1px solid var(--wpv-line);
    border-radius: 4px;
    padding: 26px;
    position: relative;
    overflow: hidden;
}
.wpv-hero-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: var(--wpv-blue);
}
.wpv-hero-card-label {
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 11px;
    color: var(--wpv-ink-mute);
    margin: 4px 0 8px;
    font-weight: 600;
}
.wpv-hero-card-stat {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 64px;
    line-height: 1;
    margin: 0;
    color: var(--wpv-blue-deep);
    letter-spacing: -0.04em;
}
.wpv-hero-card-cap {
    color: var(--wpv-ink-mute);
    font-size: 14px;
    margin: 8px 0 22px;
}
.wpv-hero-card-sub {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding-top: 18px;
    border-top: 1px dashed var(--wpv-line);
}
.wpv-hero-card-sub div {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.wpv-hero-card-sub span {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--wpv-ink-mute);
}
.wpv-hero-card-sub strong {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 22px;
}

.wpv-section {
    max-width: 1180px;
    margin: 0 auto;
    padding: 56px 24px;
}
.wpv-section--picker { background: linear-gradient(180deg, transparent, #fff 35%); }
.wpv-section-head { max-width: 720px; margin-bottom: 28px; }
.wpv-section-kicker {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11px;
    color: var(--wpv-blue);
    font-weight: 700;
    margin: 0 0 8px;
}
.wpv-section-title {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: clamp(28px, 3.4vw, 40px);
    letter-spacing: -0.02em;
    margin: 0 0 10px;
    line-height: 1.1;
}
.wpv-section-desc { color: var(--wpv-ink-mute); margin: 0; font-size: 16px; }

.wpv-filterbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 22px;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid var(--wpv-line);
    border-radius: 4px;
}
.wpv-filterbar-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--wpv-ink-mute);
    margin-right: 6px;
}
.wpv-chip {
    border: 1px solid var(--wpv-line);
    background: #fff;
    color: var(--wpv-ink);
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 13px;
    cursor: pointer;
    font-family: inherit;
}
.wpv-chip:hover { border-color: var(--wpv-blue); }
.wpv-chip--on {
    background: var(--wpv-blue);
    border-color: var(--wpv-blue);
    color: #fff;
}

.wpv-cardgrid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 18px;
}
.wpv-card {
    background: #fff;
    border: 1px solid var(--wpv-line);
    border-radius: 4px;
    padding: 22px 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: relative;
    transition: border-color .15s, box-shadow .15s;
}
.wpv-card:hover {
    border-color: #94a3b8;
    box-shadow: 0 4px 14px rgba(15,23,42,0.05);
}
.wpv-card--picked {
    border-color: var(--wpv-blue);
    box-shadow: 0 0 0 2px var(--wpv-blue-soft);
}
.wpv-card-head {
    border-bottom: 1px dashed var(--wpv-line);
    padding-bottom: 12px;
}
.wpv-card-hersteller {
    margin: 0;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--wpv-blue-deep);
    font-weight: 600;
}
.wpv-card-modell {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    margin: 4px 0 6px;
    line-height: 1.15;
}
.wpv-card-typ {
    margin: 0;
    font-size: 12px;
    color: var(--wpv-ink-mute);
}
.wpv-card-stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px 14px;
    margin: 0;
}
.wpv-card-stats div { display: flex; flex-direction: column; gap: 2px; }
.wpv-card-stats dt {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--wpv-ink-mute);
    margin: 0;
}
.wpv-card-stats dd {
    margin: 0;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 16px;
}
.wpv-card-stats-price dd { color: var(--wpv-blue-deep); }
.wpv-card-highlight {
    font-size: 13px;
    color: var(--wpv-ink-mute);
    margin: 0;
    border-left: 3px solid var(--wpv-blue-soft);
    padding-left: 10px;
}
.wpv-card-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: auto;
}
.wpv-slotpick {
    background: #f8fafc;
    border: 1px solid var(--wpv-line);
    padding: 9px 12px;
    text-align: left;
    font-size: 13px;
    border-radius: 2px;
    cursor: pointer;
    font-family: inherit;
    color: var(--wpv-ink);
    transition: background .15s, border-color .15s;
}
.wpv-slotpick:hover { border-color: var(--wpv-blue); background: #fff; }
.wpv-slotpick--filled {
    background: var(--wpv-blue);
    border-color: var(--wpv-blue);
    color: #fff;
    font-weight: 600;
}
.wpv-slotpick--busy { opacity: .4; cursor: pointer; }

.wpv-section--compare {
    background: #0b1220;
    color: #fff;
    max-width: none;
    padding: 64px 24px;
}
.wpv-section--compare .wpv-section-head { margin: 0 auto 28px; max-width: 1180px; }
.wpv-section--compare .wpv-section-title,
.wpv-section--compare .wpv-section-desc { color: #fff; }
.wpv-section--compare .wpv-section-desc { color: #cbd5e1; }
.wpv-section--compare .wpv-section-kicker { color: #38bdf8; }

.wpv-compare-wrap {
    max-width: 1180px;
    margin: 0 auto;
}

.wpv-empty {
    max-width: 1180px;
    margin: 0 auto;
    padding: 32px;
    border: 1px dashed #475569;
    border-radius: 4px;
    text-align: center;
    color: #cbd5e1;
}

.wpv-pricehero {
    background: linear-gradient(135deg, var(--wpv-blue) 0%, var(--wpv-blue-deep) 100%);
    color: #fff;
    padding: 36px 32px;
    border-radius: 4px;
    margin-bottom: 24px;
    text-align: center;
    position: relative;
    overflow: hidden;
}
.wpv-pricehero::after {
    content: 'CHEAPEST';
    position: absolute;
    top: 12px;
    right: -36px;
    transform: rotate(35deg);
    background: var(--wpv-gold);
    color: var(--wpv-ink);
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.18em;
    padding: 4px 44px;
}
.wpv-pricehero-label {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 12px;
    margin: 0;
    opacity: .85;
    font-weight: 600;
}
.wpv-pricehero-name {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 18px;
    margin: 12px 0 6px;
    letter-spacing: -0.01em;
}
.wpv-pricehero-price {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: clamp(56px, 8vw, 96px);
    line-height: 1;
    margin: 6px 0 14px;
    letter-spacing: -0.04em;
}
.wpv-pricehero-note {
    font-size: 13px;
    opacity: .85;
    margin: 0;
    max-width: 540px;
    margin-left: auto;
    margin-right: auto;
}

.wpv-tablewrap {
    background: #fff;
    color: var(--wpv-ink);
    border-radius: 4px;
    overflow-x: auto;
}
.wpv-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 640px;
}
.wpv-table th, .wpv-table td {
    padding: 14px 18px;
    text-align: left;
    border-bottom: 1px solid var(--wpv-line);
    font-size: 14px;
    vertical-align: top;
}
.wpv-table th[scope="row"] {
    font-weight: 600;
    color: var(--wpv-ink-mute);
    background: #f8fafc;
    width: 220px;
    font-size: 13px;
}
.wpv-table-colhead {
    background: var(--wpv-blue);
    color: #fff;
    border-bottom: none;
    vertical-align: top;
}
.wpv-table-colhead--empty { background: #475569; }
.wpv-th-hersteller {
    margin: 0;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: .85;
    font-weight: 600;
}
.wpv-th-modell {
    margin: 4px 0 6px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: -0.01em;
}
.wpv-th-remove {
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(255,255,255,0.4);
    color: #fff;
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 2px;
    cursor: pointer;
    font-family: inherit;
}
.wpv-th-empty {
    margin: 0;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 14px;
}
.wpv-th-empty-hint { margin: 4px 0 0; font-size: 11px; opacity: .75; }
.wpv-td-best {
    background: var(--wpv-best);
    border-left: 3px solid var(--wpv-best-line);
    font-weight: 600;
}
.wpv-td-note { color: var(--wpv-ink-mute); font-size: 13px; }

.wpv-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
    margin: 24px 0;
}
.wpv-summary-pill {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.15);
    padding: 14px 16px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.wpv-summary-pill span {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: .75;
}
.wpv-summary-pill strong {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 15px;
}
.wpv-summary-pill--price { border-left: 4px solid var(--wpv-gold); }
.wpv-summary-pill--scop { border-left: 4px solid #38bdf8; }
.wpv-summary-pill--quiet { border-left: 4px solid #a3e635; }

.wpv-ctablock { max-width: 480px; margin: 0 auto; }

.wpv-section--form { padding-top: 40px; padding-bottom: 80px; }
.wpv-form {
    background: #fff;
    border: 1px solid var(--wpv-line);
    border-radius: 4px;
    padding: 28px;
    max-width: 880px;
}
.wpv-form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 18px;
}
.wpv-field { display: flex; flex-direction: column; gap: 6px; }
.wpv-field--wide { grid-column: 1 / -1; }
.wpv-field span {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--wpv-ink-mute);
    font-weight: 600;
}
.wpv-field input, .wpv-field select {
    padding: 10px 12px;
    border: 1px solid var(--wpv-line);
    border-radius: 2px;
    font-size: 15px;
    font-family: inherit;
    background: #fff;
}
.wpv-field input:focus, .wpv-field select:focus {
    outline: 2px solid var(--wpv-blue);
    outline-offset: 1px;
    border-color: var(--wpv-blue);
}
.wpv-consent {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 14px 0 20px;
    font-size: 12px;
    color: var(--wpv-ink-mute);
    line-height: 1.5;
}
.wpv-consent input { margin-top: 3px; }
.wpv-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    padding: 10px 14px;
    border-radius: 2px;
    font-size: 14px;
    margin: 0 0 16px;
}
.wpv-form-foot {
    font-size: 12px;
    color: var(--wpv-ink-mute);
    margin: 16px 0 0;
    text-align: center;
}
.wpv-thanks {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    padding: 32px;
    border-radius: 4px;
    max-width: 880px;
}
.wpv-thanks-kicker {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11px;
    color: #047857;
    font-weight: 700;
    margin: 0 0 6px;
}
.wpv-thanks h3 {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 22px;
    margin: 0 0 8px;
}
.wpv-thanks p { margin: 0; color: var(--wpv-ink-mute); }

.wpv-foot {
    border-top: 1px solid var(--wpv-line);
    background: #fff;
    padding: 32px 24px 48px;
}
.wpv-foot-inner {
    max-width: 1180px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    text-align: center;
}
.wpv-byline { margin: 0; font-size: 13px; color: var(--wpv-ink-mute); }
.wpv-foot-note { margin: 4px 0 0; font-size: 12px; color: var(--wpv-ink-mute); max-width: 640px; }
.wpv-foot-links {
    margin: 12px 0 0;
    font-size: 13px;
    display: flex;
    gap: 10px;
    color: var(--wpv-ink-mute);
}
.wpv-foot-links a {
    color: var(--wpv-blue-deep);
    text-decoration: none;
}
.wpv-foot-links a:hover { text-decoration: underline; }

/* ── Trust strip (images + cites + byline) — wpv-* unique treatment ── */
.wpv-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.wpv-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .wpv-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.wpv-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid rgba(20,17,13,0.10); border-left: 6px solid #0369A1; box-shadow: 0 6px 22px -14px rgba(20,17,13,0.20);
}
.wpv-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.wpv-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.wpv-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.wpv-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.wpv-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.wpv-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #0369A155;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.wpv-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.wpv-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #0369A1;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.wpv-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.wpv-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.wpv-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.wpv-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.wpv-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.wpv-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
